import Decimal from 'decimal.js'
import type { LigneDevis } from '../db/modele'
import { totalLigne } from './money'
import { versCalcul } from './pdf/devis-pdf'

// Remise commerciale : une ligne à quantité −1 et prix positif, au taux de TVA des lignes remisées. C'est la forme
// la plus simple et la plus portable : les totaux, la TVA ventilée par taux, les situations, les acomptes et la
// facture électronique la comprennent sans rien changer (une quantité négative est valide en EN 16931 ; une ligne
// à quantité −1 passe Mustang et le validateur de Super PDP, voir `poc/facturx`). Le prix net d'un article n'est donc
// jamais « caché » dans une ligne : la remise se lit sur le devis.

/** Une ligne de remise est une ligne à quantité négative (créée par `lignesDeRemise`). */
export const estLigneRemise = (l: Pick<LigneDevis, 'quantite'>) => l.quantite < 0

/** Pourcentage de remise valide : strictement positif, 100 au plus, deux décimales au plus. */
export function pourcentageValide(p: Decimal.Value): Decimal | null {
  try {
    const v = new Decimal(p)
    return v.greaterThan(0) && v.lessThanOrEqualTo(100) && v.decimalPlaces() <= 2 ? v : null
  } catch {
    return null
  }
}

const htCents = (l: LigneDevis) => totalLigne(versCalcul(l)).ht.times(100)
const fmt = (p: Decimal) => p.toString().replace('.', ',')
const fmtTva = (t: number) => String(t).replace('.', ',')

/**
 * Lignes de remise de `pourcent` % :
 *  - sur une ligne (`cible` = son indice) : une ligne « Remise 10 % sur « Pose carrelage » » à son taux de TVA ;
 *  - sur tout le devis : une ligne par taux de TVA, calculée sur le HT des lignes (les remises déjà faites ne sont
 *    pas remisées de nouveau), « Remise 10 % (TVA 20 %) ».
 * Une remise qui s'arrondit à 0 € n'est pas créée. Le montant est arrondi au centime.
 */
export function lignesDeRemise(lignes: LigneDevis[], pourcent: Decimal.Value, cible?: number): LigneDevis[] {
  const p = pourcentageValide(pourcent)
  if (!p) return []
  const ligneRemise = (designation: string, ht: Decimal, tvaPercent: number): LigneDevis[] => {
    const montant = ht.times(p).dividedBy(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber()
    return montant > 0 ? [{ designation, unite: 'u', quantite: -1, prixUnitaireHtCents: montant, tvaPercent }] : []
  }
  if (cible !== undefined) {
    const l = lignes[cible]
    return l && !estLigneRemise(l) ? ligneRemise(`Remise ${fmt(p)} % sur « ${l.designation} »`, htCents(l), l.tvaPercent) : []
  }
  const parTaux = new Map<number, Decimal>()
  for (const l of lignes) if (!estLigneRemise(l)) parTaux.set(l.tvaPercent, (parTaux.get(l.tvaPercent) ?? new Decimal(0)).plus(htCents(l)))
  const taux = [...parTaux.keys()].sort((a, b) => a - b)
  return taux.flatMap((t) => ligneRemise(taux.length > 1 ? `Remise ${fmt(p)} % (TVA ${fmtTva(t)} %)` : `Remise ${fmt(p)} %`, parTaux.get(t)!, t))
}

/** Insère les lignes de remise : juste sous la ligne remisée, ou à la fin du devis pour une remise générale. */
export function avecRemise(lignes: LigneDevis[], pourcent: Decimal.Value, cible?: number): LigneDevis[] {
  const remises = lignesDeRemise(lignes, pourcent, cible)
  if (remises.length === 0) return lignes
  if (cible === undefined) return [...lignes, ...remises]
  return [...lignes.slice(0, cible + 1), ...remises, ...lignes.slice(cible + 1)]
}
